/**
 * Supply templates — 3 documents.
 *
 * ingredient-supply-agreement, packaging-supply-agreement, master-supply-agreement.
 */
export const SUPPLY_TEMPLATES = [
  {
    id: "ingredient-supply-agreement",
    docId: "ingredient-supply-agreement",
    title: "INGREDIENT SUPPLY AGREEMENT",
    version: "1.0",
    lastReviewed: "2026-08-25",

    founderGuide: {
      useThisWhen:
        "You buy an ingredient directly instead of letting the co-packer source it, or a single ingredient matters enough that losing access to it would stop production. Put it in place before the second order, not after the first shortage.",
      whyItMatters:
        "One ingredient can hold a whole product hostage. A functional botanical, a specific protein isolate, a flavour system built for you — if the supplier allocates you out during a shortage, changes origin without telling you, or raises price knowing you have no second source, that is not a procurement problem, it is a product you cannot make. The terms deciding all three are the ones founders skim because the first order was small.",
      negotiateCarefully: [
        {
          point: "How price is allowed to move",
          detail:
            "An escalation clause tied to the supplier's own assessment of its costs lets price rise on assertion alone. Tie any increase to a documented, verifiable basis, require notice, and give yourself the right to place a final order at the old price and walk if you will not accept the new one.",
        },
        {
          point: "Allocation during a shortage",
          detail:
            "With no allocation rule, a tight market means the largest customers get supplied and a small brand simply stops. Ask for a stated baseline volume protected during allocation, measured against your recent purchase history rather than the supplier's discretion.",
        },
        {
          point: "Substitution, origin and sub-supplier changes",
          detail:
            "A change of growing region, processing site or sub-supplier can alter allergen status, certification eligibility and how the ingredient performs, while the paperwork looks unchanged. Require notice and approval before any of them, not a note on the certificate afterwards.",
        },
        {
          point: "The rejection window and what the remedy reaches",
          detail:
            "A short window running from delivery plus a replacement-only remedy means a bad lot found during production costs you a full run and recovers the price of the ingredient. Run the window from first use, and push the remedy toward the cost of the affected run.",
        },
        {
          point: "Minimums against shelf life, and exit",
          detail:
            "Ingredient shelf life is often shorter than the time a young brand needs to consume a supplier minimum, and an annual volume commitment set at launch projections becomes a bill for material you never used. Check both against a forecast you believe, and secure a last-time-buy right and transition period if the supplier discontinues the material.",
        },
      ],
      redFlags: [
        "Price changeable at the supplier's discretion with no notice.",
        "No allocation commitment of any kind during a shortage.",
        "Silence on substitution, origin or sub-supplier changes.",
        "A rejection window measured in a few days from delivery.",
        "Volume commitments with no relief mechanism if demand shifts.",
        "No traceability or recall cooperation obligation.",
      ],
      timeToComplete: "30 minutes to fill · 1 to 2 weeks to agree specification and pricing",
    },

    placeholders: [
      { token: "[EFFECTIVE DATE]", label: "Effective date", example: "September 1, 2026" },
      { token: "[COMPANY NAME]", label: "Your legal entity name", brandKey: "legalEntityName", example: "Acme Beverage Co., LLC" },
      { token: "[COMPANY STATE]", label: "Your state of formation", brandKey: "stateOfFormation", example: "Delaware" },
      { token: "[COMPANY ENTITY TYPE]", label: "Your entity type", example: "limited liability company" },
      { token: "[COMPANY ADDRESS]", label: "Your address", brandKey: "principalAddress", example: "123 Main St, Austin, TX 78701" },
      { token: "[SUPPLIER NAME]", label: "Supplier's legal name", example: "Cascade Botanicals, Inc." },
      { token: "[SUPPLIER STATE]", label: "Supplier's state of formation", example: "Oregon" },
      { token: "[SUPPLIER ENTITY TYPE]", label: "Supplier's entity type", example: "corporation" },
      { token: "[SUPPLIER ADDRESS]", label: "Supplier's address", example: "1400 River Rd, Salem, OR 97301" },
      { token: "[BRAND NAME]", label: "Your brand name", brandKey: "tradeName", example: "Acme" },
      { token: "[PRODUCT CATEGORY]", label: "Product category", brandKey: "productCategory", example: "functional beverage" },
      { token: "[INGREDIENT NAME]", label: "Ingredient supplied", example: "Organic yuzu juice concentrate 5-fold" },
      { token: "[SPECIFICATION REFERENCE]", label: "Specification document and version", example: "Spec CB-YZ-05, version 2, dated July 2026" },
      { token: "[CERTIFICATIONS]", label: "Certifications the supplier must maintain", example: "organic, kosher, non-GMO" },
      { token: "[PRICE]", label: "Price", example: "18.40" },
      { token: "[PRICE UNIT]", label: "Unit the price is quoted per", example: "kilogram" },
      { token: "[PRICE VALIDITY PERIOD]", label: "How long the price holds", example: "12 months from the Effective Date" },
      { token: "[ESCALATION BASIS]", label: "Documented basis for any price change", example: "a published commodity index the parties name in writing" },
      { token: "[PRICE CHANGE NOTICE]", label: "Notice before a price change", example: "90 days" },
      { token: "[MINIMUM ORDER QUANTITY]", label: "Minimum order quantity", example: "200 kg" },
      { token: "[ANNUAL VOLUME COMMITMENT]", label: "Annual volume commitment, or 'none'", example: "none" },
      { token: "[LEAD TIME]", label: "Lead time from order to delivery", example: "5 weeks" },
      { token: "[FORECAST HORIZON]", label: "Rolling forecast horizon", example: "6 months" },
      { token: "[BINDING FORECAST WINDOW]", label: "Portion of the forecast that is binding", example: "the first 30 days" },
      { token: "[ALLOCATION BASELINE]", label: "Volume protected during allocation", example: "the average monthly volume purchased over the preceding 12 months" },
      { token: "[DELIVERY TERMS]", label: "Delivery terms and destination", example: "delivered to the Manufacturer's Columbus, OH plant" },
      { token: "[PAYMENT DAYS]", label: "Payment terms in days", example: "30 days" },
      { token: "[REJECTION WINDOW]", label: "Window to reject non-conforming material", example: "30 days from first use, and in any event within the material's shelf life" },
      { token: "[TRACEABILITY RESPONSE TIME]", label: "Time to respond to a traceability request", example: "4 hours" },
      { token: "[SUPPLY INTERRUPTION NOTICE]", label: "Notice of an anticipated supply interruption", example: "as soon as known, and in any event within 2 business days" },
      { token: "[DISCONTINUATION NOTICE]", label: "Notice before discontinuing the ingredient", example: "12 months" },
      { token: "[LAST-TIME-BUY WINDOW]", label: "Window to place a final order", example: "6 months from the discontinuation notice" },
      { token: "[TERM LENGTH]", label: "Initial term", example: "2 years" },
      { token: "[RENEWAL TERM]", label: "Renewal term", example: "1 year" },
      { token: "[TERMINATION NOTICE]", label: "Notice to terminate or not renew", example: "90 days" },
      { token: "[CURE PERIOD]", label: "Cure period for a material breach", example: "30 days" },
      { token: "[INSURANCE REQUIREMENTS]", label: "Insurance the supplier must carry", example: "commercial general liability including products liability, with the Brand named as additional insured" },
      { token: "[GOVERNING LAW STATE]", label: "Governing law state", example: "Delaware" },
      { token: "[VENUE]", label: "Venue for disputes", example: "Wilmington, Delaware" },
    ],

    body: `This Ingredient Supply Agreement (this "Agreement") is entered into as of [EFFECTIVE DATE]
(the "Effective Date") by and between:

  [COMPANY NAME], a [COMPANY STATE] [COMPANY ENTITY TYPE], with a principal place of business
  at [COMPANY ADDRESS] (the "Brand"); and

  [SUPPLIER NAME], a [SUPPLIER STATE] [SUPPLIER ENTITY TYPE], with a principal place of business
  at [SUPPLIER ADDRESS] (the "Supplier").

1. SCOPE

1.1 The Supplier shall supply, and the Brand may purchase, [INGREDIENT NAME] (the "Material") for
    use in [PRODUCT CATEGORY] products sold under the [BRAND NAME] brand.

1.2 The Brand may direct that Material be delivered to, and invoiced through, a manufacturer it
    designates in writing. The commercial relationship and the rights under this Agreement remain
    with the Brand, and the Supplier shall copy the Brand on order confirmations, pricing and
    delivery notices sent to that manufacturer.

2. SPECIFICATION

2.1 All Material must conform to [SPECIFICATION REFERENCE] (the "Specification"), which is
    incorporated into this Agreement.

2.2 The Specification may be amended only by a written document signed by both parties. Where the
    Material is agricultural or botanical in origin, the Specification states the ranges that
    accommodate normal seasonal and regional variation, and Material within those ranges conforms.

2.3 The Supplier shall maintain the following certifications for the Material and its production
    site, and shall provide current documents on request: [CERTIFICATIONS]. The Supplier shall
    notify the Brand in writing promptly if any of them lapses, is suspended, or is at risk.

2.4 The Supplier shall provide, and keep current, the documentation relating to the Material that
    the Brand reasonably requires in order to support the Brand's labelling, claims and customer
    requirements, including facility registration details, food-use assurances, allergen
    statements and the documentation behind each certification. Responsibility for deciding what
    the Brand's label says and what claims it makes rests with the Brand; responsibility for the
    accuracy and currency of the Material documentation rests with the Supplier.

3. PRICE

3.1 The price of the Material is [PRICE] per [PRICE UNIT], firm for [PRICE VALIDITY PERIOD].

3.2 After the validity period, the Supplier may propose a price change only by reference to
    [ESCALATION BASIS], on [PRICE CHANGE NOTICE] written notice, and the notice must identify the
    documented movement it relies on.

3.3 If the Brand does not accept a proposed increase, the Brand may place a final order at the
    existing price for delivery within the lead time, and may terminate this Agreement on written
    notice without liability for any unfulfilled volume commitment.

3.4 Invoices are payable within [PAYMENT DAYS] of receipt. The Brand may withhold amounts genuinely
    disputed in good faith, provided it pays the undisputed balance and states the basis of the
    dispute.

4. ORDERS, MINIMUMS AND FORECASTING

4.1 The minimum order quantity is [MINIMUM ORDER QUANTITY].

4.2 Any annual volume commitment is [ANNUAL VOLUME COMMITMENT]. If none is stated, the Brand has
    no minimum purchase obligation.

4.3 The lead time from a confirmed order to delivery is [LEAD TIME]. The Supplier shall notify the
    Brand promptly if a confirmed order will be late and shall state the revised delivery date.

4.4 The Brand shall provide a rolling forecast covering [FORECAST HORIZON], updated monthly.
    [BINDING FORECAST WINDOW] of each forecast is binding on both parties; the balance is a
    planning estimate and creates no purchase obligation and no supply obligation beyond
    Section 4.5.

4.5 The Supplier shall plan its own raw material procurement against the full forecast horizon and
    shall notify the Brand if it cannot support forecast volumes.

4.6 Delivery is [DELIVERY TERMS]. Each delivery must be accompanied by the documents required by
    Section 5.

5. QUALITY, CERTIFICATES OF ANALYSIS AND TRACEABILITY

5.1 The Supplier shall provide a certificate of analysis with each lot, identifying the lot code,
    the parameters tested, the test methods used, and the actual values obtained. A certificate
    stating only that the Material conforms does not satisfy this Section.

5.2 The Supplier shall retain samples and records for each lot for a period no shorter than the
    Material's shelf life plus the shelf life of the finished product it is used in, and shall
    make them available to the Brand on request.

5.3 The Supplier shall respond to a traceability request from the Brand within
    [TRACEABILITY RESPONSE TIME], identifying the lots supplied, their source materials and their
    destinations.

5.4 The Brand or its representative may audit the Supplier's production site and quality records
    on reasonable written notice, during normal business hours, no more than as reasonably
    necessary.

6. NON-CONFORMING MATERIAL

6.1 The Brand may reject any Material that does not conform to the Specification, by written
    notice given within [REJECTION WINDOW].

6.2 On rejection, the Supplier shall, at the Brand's election, replace the Material on an
    expedited basis at the Supplier's cost or credit the price paid, and shall bear the cost of
    return or disposal.

6.3 Where non-conforming Material is discovered only after it has been used in production, the
    Supplier's responsibility extends to the reasonable, documented cost of the affected
    production run, including the other ingredients, packaging and processing consumed in it. The
    parties shall discuss the treatment of any wider consequences in good faith.

6.4 Acceptance of a lot does not waive the Brand's rights in respect of a defect that was not
    reasonably discoverable on inspection.

7. CHANGES, SUBSTITUTION AND NOTIFICATION

7.1 The Supplier shall give the Brand written notice, and obtain the Brand's written approval,
    before any change to: the Specification; the composition, grade or processing of the Material;
    the country or region of origin; the production site; any sub-supplier of a component of the
    Material; the allergen profile of the production environment; or any certification status.

7.2 The Supplier shall not substitute alternative material for the Material without the Brand's
    prior written approval, whatever the reason for the substitution.

7.3 The Supplier shall notify the Brand promptly of any information suggesting that Material
    already supplied does not conform to the Specification or is unsuitable for its intended use.

8. SUPPLY INTERRUPTION AND ALLOCATION

8.1 The Supplier shall notify the Brand of any actual or anticipated interruption to supply
    [SUPPLY INTERRUPTION NOTICE], stating the cause, the expected duration and the mitigation
    planned.

8.2 If the Supplier is unable to meet total demand for the Material, it shall allocate available
    Material so that the Brand receives no less than [ALLOCATION BASELINE], and shall not
    de-prioritise the Brand by reason of its order size relative to other customers.

8.3 During any interruption, the Brand may source the Material or a substitute elsewhere without
    breaching any exclusivity or volume commitment, and any such purchases count toward any annual
    volume commitment.

8.4 Neither party is liable for a failure caused by an event beyond its reasonable control,
    provided it notifies the other promptly and works to resume performance. Ordinary movements in
    market price, currency or the Supplier's own costs are not such an event.

9. RECALL AND WITHDRAWAL COOPERATION

9.1 Each party shall notify the other promptly on becoming aware of a matter that may lead to a
    withdrawal or recall of a product containing the Material.

9.2 The Supplier shall cooperate promptly and fully in any withdrawal, recall or investigation,
    including by providing lot-level traceability, production records, retained samples and
    testing support.

9.3 Responsibility for the costs of a withdrawal or recall follows fault: each party bears the
    costs attributable to its own breach, defective material, or negligent act or omission.
    Decisions about whether to conduct a withdrawal or recall of the Brand's finished product rest
    with the Brand.

10. WARRANTIES AND INSURANCE

10.1 The Supplier warrants that Material supplied conforms to the Specification, is produced under
     sanitary conditions consistent with the certifications it maintains, is fit for use as a food
     or beverage ingredient in the manner contemplated by this Agreement, and is free of liens.

10.2 Each party shall indemnify the other against third-party claims arising from its own breach
     of this Agreement or its negligent or wilful acts.

10.3 The Supplier shall maintain [INSURANCE REQUIREMENTS], and shall provide certificates of
     insurance on request and on renewal.

11. TERM AND TERMINATION

11.1 This Agreement begins on the Effective Date and runs for [TERM LENGTH], renewing
     automatically for successive [RENEWAL TERM] periods unless either party gives
     [TERMINATION NOTICE] written notice of non-renewal.

11.2 Either party may terminate on written notice if the other materially breaches and does not
     cure within [CURE PERIOD] after notice.

11.3 The Supplier shall give the Brand at least [DISCONTINUATION NOTICE] written notice before
     discontinuing the Material or ceasing to supply it to the Brand. Following that notice, or
     following termination by the Brand for the Supplier's breach, the Brand may place final
     orders during [LAST-TIME-BUY WINDOW] at the price then in effect, in any quantity the Brand
     specifies.

11.4 On termination the Supplier shall fulfil all accepted orders, deliver the current
     Specification and all certification and traceability documentation relating to Material
     supplied, and provide reasonable assistance to the Brand in qualifying an alternative source.

11.5 Sections 5, 6, 9, 10, 12 and 13 survive termination or expiry.

12. GOVERNING LAW AND VENUE

12.1 This Agreement is governed by the laws of the State of [GOVERNING LAW STATE], without regard
     to its conflict of laws principles. The parties consent to venue in [VENUE].

13. GENERAL

13.1 This Agreement is the entire agreement between the parties on its subject matter and prevails
     over any terms printed on a quotation, purchase order, order acknowledgment, invoice or
     delivery note. It may be amended only in a writing signed by both parties. Neither party may
     assign it without the other's prior written consent, except that either party may assign to a
     successor in connection with a merger or sale of substantially all assets. Each party shall
     keep the other's non-public information confidential and use it only for this Agreement. If
     any provision is held unenforceable, the remainder continues in effect. This Agreement may be
     signed in counterparts, including by electronic signature, each of which is an original.

IN WITNESS WHEREOF, the parties have executed this Agreement as of the Effective Date.


[COMPANY NAME]                            [SUPPLIER NAME]


By: _______________________________       By: _______________________________

Name: _____________________________       Name: _____________________________

Title: ____________________________       Title: ____________________________

Date: _____________________________       Date: _____________________________
`,
  },

  {
    id: "packaging-supply-agreement",
    docId: "packaging-supply-agreement",
    title: "PACKAGING SUPPLY AGREEMENT",
    version: "1.0",
    lastReviewed: "2026-08-25",

    founderGuide: {
      useThisWhen:
        "Before ordering any custom or printed packaging component — cans, bottles, labels, cartons, closures, film — where the order is large enough or slow enough that changing your mind afterwards is expensive.",
      whyItMatters:
        "Packaging is where a young brand's cash gets trapped. Print minimums are set by the press, not by your sales, so the smallest order is often several production runs' worth. Add lead times measured in months and you have a warehouse full of components bought against a forecast. Then a formula tweak or a label revision lands and the whole minimum becomes waste. Who eats obsolete packaging is one of the most expensive terms in CPG and one of the least often written down.",
      negotiateCarefully: [
        {
          point: "Who owns the tooling, plates, dies and cylinders",
          detail:
            "If you funded them and the supplier owns them, moving suppliers means paying to make them again and waiting out the lead time. Get ownership stated in writing, along with an obligation to release them, and say how long they will be stored and maintained.",
        },
        {
          point: "Obsolescence when artwork or the formula changes",
          detail:
            "This is the clause that decides who pays when an ingredient statement change strands printed stock. Ask for a defined process: notice, a use-up window, a stated cap on your exposure, and an obligation to stop production of the old version once notice is given.",
        },
        {
          point: "Overrun and underrun tolerance, and how it is billed",
          detail:
            "A wide tolerance means being invoiced for materially more than you ordered, or receiving materially less and being unable to complete a run. Narrow it, and bill on quantity actually delivered rather than quantity produced.",
        },
        {
          point: "Artwork approval and where liability for an error lands",
          detail:
            "Sign-off usually transfers responsibility for an error to you, so the proofing step is the last point at which a mistake is cheap. Require a physical proof before every full run, keep version control explicit, and say what happens when the printer runs a superseded version.",
        },
        {
          point: "Storage of components and what it costs",
          detail:
            "Components made to a press minimum have to live somewhere, and storage charged from completion can wipe out the saving that justified the minimum. Negotiate a free storage window sized to your production cycle, with stated rates after it and a stated position on risk of loss while stored.",
        },
      ],
      redFlags: [
        "No statement of who owns tooling, plates or dies you paid for.",
        "Silence on obsolete stock when artwork or the specification changes.",
        "Wide overrun tolerance billed at full price.",
        "Storage charges beginning on completion with no free period.",
        "No inbound acceptance criteria or defect remedy.",
        "A price quoted with no validity period on a component with a long lead time.",
      ],
      timeToComplete: "30 minutes to fill · 1 to 2 weeks to agree artwork and tooling terms",
    },

    placeholders: [
      { token: "[EFFECTIVE DATE]", label: "Effective date", example: "September 1, 2026" },
      { token: "[COMPANY NAME]", label: "Your legal entity name", brandKey: "legalEntityName", example: "Acme Beverage Co., LLC" },
      { token: "[COMPANY STATE]", label: "Your state of formation", brandKey: "stateOfFormation", example: "Delaware" },
      { token: "[COMPANY ENTITY TYPE]", label: "Your entity type", example: "limited liability company" },
      { token: "[COMPANY ADDRESS]", label: "Your address", brandKey: "principalAddress", example: "123 Main St, Austin, TX 78701" },
      { token: "[SUPPLIER NAME]", label: "Supplier's legal name", example: "Great Lakes Container Co." },
      { token: "[SUPPLIER STATE]", label: "Supplier's state of formation", example: "Michigan" },
      { token: "[SUPPLIER ENTITY TYPE]", label: "Supplier's entity type", example: "corporation" },
      { token: "[SUPPLIER ADDRESS]", label: "Supplier's address", example: "900 Foundry St, Grand Rapids, MI 49503" },
      { token: "[BRAND NAME]", label: "Your brand name", brandKey: "tradeName", example: "Acme" },
      { token: "[PRODUCT CATEGORY]", label: "Product category", brandKey: "productCategory", example: "functional beverage" },
      { token: "[COMPONENT LIST]", label: "Components covered", example: "printed 12 oz sleek cans, ends, and 12-count trays" },
      { token: "[SPECIFICATION REFERENCE]", label: "Component specification and version", example: "Spec GL-CAN-12, version 4, dated June 2026" },
      { token: "[FILLING LINE]", label: "Filling line the components must run on", example: "the Manufacturer's line 3 in Columbus, OH" },
      { token: "[PRICE SCHEDULE]", label: "Price tiers by quantity", example: "see Schedule A price grid" },
      { token: "[PRICE VALIDITY PERIOD]", label: "How long a quoted price holds", example: "through the quoted lead time, and in any event 90 days" },
      { token: "[ESCALATION BASIS]", label: "Documented basis for any price change", example: "a published substrate index the parties name in writing" },
      { token: "[PRICE CHANGE NOTICE]", label: "Notice before a price change", example: "60 days" },
      { token: "[PRESS MINIMUM]", label: "Minimum per print or forming run", example: "150,000 cans per design" },
      { token: "[MINIMUM ORDER QUANTITY]", label: "Minimum order quantity per release", example: "one pallet layer" },
      { token: "[LEAD TIME]", label: "Lead time from artwork approval to delivery", example: "10 weeks" },
      { token: "[FORECAST HORIZON]", label: "Rolling forecast horizon", example: "6 months" },
      { token: "[BINDING FORECAST WINDOW]", label: "Portion of the forecast that is binding", example: "the first 60 days" },
      { token: "[OVERRUN TOLERANCE]", label: "Overrun and underrun tolerance", example: "2 percent either way" },
      { token: "[PROOF TYPE]", label: "Proof required before a full run", example: "a physical press proof on production substrate" },
      { token: "[ARTWORK APPROVER]", label: "Who signs off artwork for you", example: "the Brand's named brand manager" },
      { token: "[CHANGE NOTICE PERIOD]", label: "Notice of an artwork or specification change", example: "30 days" },
      { token: "[USE-UP WINDOW]", label: "Window to use up superseded stock", example: "90 days, or until existing stock is consumed" },
      { token: "[OBSOLESCENCE CAP]", label: "Cap on your obsolescence exposure", example: "components produced or in production against a released order, plus documented substrate committed" },
      { token: "[FREE STORAGE PERIOD]", label: "Free storage period", example: "90 days from completion" },
      { token: "[STORAGE RATE]", label: "Storage rate after the free period", example: "the rate stated in Schedule A" },
      { token: "[DELIVERY TERMS]", label: "Delivery terms and destination", example: "delivered to the Manufacturer's Columbus, OH plant" },
      { token: "[PAYMENT DAYS]", label: "Payment terms in days", example: "30 days" },
      { token: "[REJECTION WINDOW]", label: "Window to reject non-conforming components", example: "30 days from delivery, or on first use if the defect is not visible on inspection" },
      { token: "[DEFECT THRESHOLD]", label: "Acceptable defect rate", example: "the rate stated in the Specification" },
      { token: "[TOOLING DESCRIPTION]", label: "Tooling, plates and dies you are funding", example: "printing plates, cylinders and the neck die" },
      { token: "[TOOLING RELEASE DAYS]", label: "Days to release tooling on request", example: "20 business days" },
      { token: "[TERM LENGTH]", label: "Initial term", example: "2 years" },
      { token: "[RENEWAL TERM]", label: "Renewal term", example: "1 year" },
      { token: "[TERMINATION NOTICE]", label: "Notice to terminate or not renew", example: "90 days" },
      { token: "[CURE PERIOD]", label: "Cure period for a material breach", example: "30 days" },
      { token: "[GOVERNING LAW STATE]", label: "Governing law state", example: "Delaware" },
      { token: "[VENUE]", label: "Venue for disputes", example: "Wilmington, Delaware" },
    ],

    body: `This Packaging Supply Agreement (this "Agreement") is entered into as of [EFFECTIVE DATE]
(the "Effective Date") by and between:

  [COMPANY NAME], a [COMPANY STATE] [COMPANY ENTITY TYPE], with a principal place of business
  at [COMPANY ADDRESS] (the "Brand"); and

  [SUPPLIER NAME], a [SUPPLIER STATE] [SUPPLIER ENTITY TYPE], with a principal place of business
  at [SUPPLIER ADDRESS] (the "Supplier").

1. SCOPE

1.1 The Supplier shall supply, and the Brand may purchase, the packaging components described as
    [COMPONENT LIST] (the "Components") for [PRODUCT CATEGORY] products sold under the
    [BRAND NAME] brand.

1.2 The Components must conform to [SPECIFICATION REFERENCE] (the "Specification") and must run on
    [FILLING LINE], including as to dimensions, neck finish, closure application, label wind
    direction, tray and case dimensions and palletisation.

1.3 The Brand may direct that Components be delivered to a manufacturer it designates in writing.
    The commercial relationship and the rights under this Agreement remain with the Brand.

2. ARTWORK, PROOFS AND VERSION CONTROL

2.1 The Brand supplies artwork. Each artwork release carries a version number and a date, and the
    Supplier shall record the version used on every production run and on every delivery document.

2.2 Before each full production run, the Supplier shall provide [PROOF TYPE] and shall not proceed
    until the proof is approved in writing by [ARTWORK APPROVER].

2.3 The Supplier is responsible for producing Components that match the approved proof and the
    Specification, including colour targets and any stated tolerances. The Brand is responsible for
    the content of the artwork it approves, including label copy, ingredient statements and claims.

2.4 If the Supplier runs a version other than the latest approved version, the Supplier bears the
    cost of the affected Components and of replacing them on an expedited basis.

2.5 The Supplier shall not use the Brand's artwork, marks or Components for any purpose other than
    supplying the Brand, shall not supply Components bearing the Brand's artwork to any other
    person, and shall destroy or return obsolete artwork files on request.

3. TOOLING, PLATES AND DIES

3.1 The Brand owns [TOOLING DESCRIPTION] and all other tooling, plates, cylinders, dies, moulds and
    fixtures paid for by the Brand, whether in whole or in part, and whether or not held at the
    Supplier's site (together, the "Tooling").

3.2 The Supplier holds the Tooling as bailee, shall identify it as the Brand's property in its
    records, shall not use it for any other customer, and shall insure it against loss or damage
    while in its possession.

3.3 The Supplier shall maintain the Tooling in working condition and shall notify the Brand when
    the Tooling approaches the end of its useful life.

3.4 On the Brand's written request, and on any termination of this Agreement, the Supplier shall
    release the Tooling to the Brand or to a party the Brand designates within
    [TOOLING RELEASE DAYS], and shall not assert any lien or retention right over it on account of
    a commercial dispute where undisputed invoices are current.

4. PRICE

4.1 Prices are as set out in [PRICE SCHEDULE], and each quoted price is firm for
    [PRICE VALIDITY PERIOD] so that a quote survives through the lead time to delivery.

4.2 After the validity period, the Supplier may propose a price change only by reference to
    [ESCALATION BASIS] and on [PRICE CHANGE NOTICE] written notice, stating the documented movement
    relied on.

4.3 If the Brand does not accept a proposed increase, the Brand may place a final order at the
    existing price and may terminate this Agreement on written notice.

4.4 Invoices are payable within [PAYMENT DAYS] of receipt.

5. ORDERS, MINIMUMS AND LEAD TIMES

5.1 The press or forming minimum per design is [PRESS MINIMUM]. The minimum quantity per release
    against a produced order is [MINIMUM ORDER QUANTITY].

5.2 The lead time from artwork approval to delivery is [LEAD TIME]. The Supplier shall notify the
    Brand promptly if a confirmed order will be late and shall state the revised date.

5.3 The Brand shall provide a rolling forecast covering [FORECAST HORIZON], updated monthly.
    [BINDING FORECAST WINDOW] of each forecast is binding; the balance is a planning estimate and
    creates no purchase obligation.

5.4 Overrun and underrun tolerance is [OVERRUN TOLERANCE]. The Brand is invoiced for the quantity
    actually delivered and accepted, not for the quantity produced. An underrun outside the
    tolerance is a shortfall the Supplier shall make good at its cost.

5.5 Delivery is [DELIVERY TERMS].

6. STORAGE OF COMPONENTS

6.1 Where a press minimum produces more Components than a single release, the Supplier shall store
    the balance free of charge for [FREE STORAGE PERIOD], and thereafter at [STORAGE RATE].

6.2 Stored Components are held at the Supplier's risk and must be stored in conditions that protect
    them from damage, contamination and deterioration, in a manner consistent with the
    Specification.

6.3 The Supplier shall release stored Components in the order produced unless the Brand directs
    otherwise, and shall report stored quantities to the Brand monthly.

7. ACCEPTANCE AND DEFECTS

7.1 The Brand or its designated manufacturer may reject Components that do not conform to the
    Specification or the approved proof, by written notice within [REJECTION WINDOW].

7.2 The acceptable defect rate is [DEFECT THRESHOLD]. Components exceeding it are non-conforming
    for the whole delivery.

7.3 On rejection, the Supplier shall replace the Components on an expedited basis at its cost or
    credit the price paid, at the Brand's election, and shall bear return or disposal costs.

7.4 Where a defect is discovered only during filling, the Supplier's responsibility extends to the
    reasonable, documented cost of the affected production run, including the product, other
    components and line time consumed. The parties shall discuss wider consequences in good faith.

7.5 Where Components are delivered directly to a manufacturer, that manufacturer's receipt does not
    constitute acceptance by the Brand, and the inspection obligations in the Specification apply
    on first use as well as on receipt.

8. CHANGES AND OBSOLESCENCE

8.1 The Supplier shall give the Brand written notice, and obtain written approval, before any
    change to the Specification, the substrate, the printing process, the production site or any
    sub-supplier.

8.2 The Brand may change artwork or the Specification on [CHANGE NOTICE PERIOD] written notice. On
    receiving notice, the Supplier shall stop producing the superseded version and shall confirm
    in writing the quantities of finished Components, work in progress and committed substrate held
    for the Brand.

8.3 The Brand and the Supplier shall use a [USE-UP WINDOW] to consume superseded Components where
    the change permits it.

8.4 The Brand's responsibility for Components rendered obsolete by a change it makes is limited to
    [OBSOLESCENCE CAP]. The Supplier shall mitigate by using compatible substrate on other work
    where it can and shall credit the value recovered.

8.5 Where Components are rendered obsolete by the Supplier's error, by a change the Supplier makes,
    or by the Supplier producing a superseded version, the cost is the Supplier's.

8.6 Obsolete Components bearing the Brand's artwork must be destroyed rather than sold or
    otherwise disposed of, and the Supplier shall certify destruction in writing.

9. WARRANTIES AND SUPPLY INTERRUPTION

9.1 The Supplier warrants that Components conform to the Specification and the approved proof, are
    suitable for their intended food or beverage contact use as contemplated by this Agreement, and
    are free of liens.

9.2 The Supplier shall notify the Brand promptly of any actual or anticipated interruption to
    supply, stating the cause, expected duration and planned mitigation, and shall not
    de-prioritise the Brand by reason of its order size relative to other customers.

9.3 During an interruption the Brand may source Components elsewhere without breaching this
    Agreement.

9.4 Each party shall cooperate promptly with the other in any withdrawal, recall or traceability
    investigation involving the Components, including providing production and lot records.

9.5 Neither party is liable for a failure caused by an event beyond its reasonable control,
    provided it notifies the other promptly and works to resume performance. Ordinary movements in
    market price or the Supplier's own costs are not such an event.

10. TERM AND TERMINATION

10.1 This Agreement begins on the Effective Date and runs for [TERM LENGTH], renewing
     automatically for successive [RENEWAL TERM] periods unless either party gives
     [TERMINATION NOTICE] written notice of non-renewal.

10.2 Either party may terminate on written notice if the other materially breaches and does not
     cure within [CURE PERIOD] after notice.

10.3 On termination the Supplier shall: fulfil all accepted orders; release the Tooling under
     Section 3.4; deliver or make available all Components and substrate held for the Brand, which
     the Brand may purchase at the price applicable to them; and destroy any remaining artwork
     files and obsolete Components bearing the Brand's artwork.

10.4 Sections 2.5, 3, 7, 8, 9, 11 and 12 survive termination or expiry.

11. GOVERNING LAW AND VENUE

11.1 This Agreement is governed by the laws of the State of [GOVERNING LAW STATE], without regard
     to its conflict of laws principles. The parties consent to venue in [VENUE].

12. GENERAL

12.1 This Agreement is the entire agreement between the parties on its subject matter and prevails
     over any terms printed on a quotation, purchase order, order acknowledgment, invoice or
     delivery note. It may be amended only in a writing signed by both parties. Neither party may
     assign it without the other's prior written consent, except that either party may assign to a
     successor in connection with a merger or sale of substantially all assets. Each party shall
     keep the other's non-public information confidential and use it only for this Agreement. If
     any provision is held unenforceable, the remainder continues in effect. This Agreement may be
     signed in counterparts, including by electronic signature, each of which is an original.

IN WITNESS WHEREOF, the parties have executed this Agreement as of the Effective Date.


[COMPANY NAME]                            [SUPPLIER NAME]


By: _______________________________       By: _______________________________

Name: _____________________________       Name: _____________________________

Title: ____________________________       Title: ____________________________

Date: _____________________________       Date: _____________________________
`,
  },

  {
    id: "master-supply-agreement",
    docId: "master-supply-agreement",
    title: "MASTER SUPPLY AGREEMENT",
    version: "1.0",
    lastReviewed: "2026-08-25",

    founderGuide: {
      useThisWhen:
        "You will buy repeatedly from the same supplier — often more than one material — and want one negotiated set of terms sitting over every purchase order, instead of renegotiating each time or, more commonly, agreeing nothing at all.",
      whyItMatters:
        "Without a master agreement, every purchase order is governed by whatever terms were attached to it, which in practice means the supplier's. The result is a two-year relationship with no agreed position on quality, on what happens when supply fails, or on who pays when a bad lot ruins a production run. A master agreement is also what makes forecasting safe: it is where you draw the line between a planning number and a commitment to buy.",
      negotiateCarefully: [
        {
          point: "Order of precedence",
          detail:
            "Without it, a supplier's order acknowledgment can override the master agreement you negotiated, one purchase order at a time. State the ranking explicitly and say that pre-printed terms on any form have no effect.",
        },
        {
          point: "What a schedule is allowed to change",
          detail:
            "Schedules are how one agreement covers several materials, but a schedule that can silently rewrite the liability or termination terms defeats the point. Let schedules set commercial detail, and require an express, signed amendment to move anything else.",
        },
        {
          point: "The binding portion of the forecast",
          detail:
            "A forecast that becomes a purchase obligation with no stated window turns planning into a commitment you did not know you had made. Define the binding window narrowly and label the rest as an estimate in the document itself.",
        },
        {
          point: "The liability cap and what sits outside it",
          detail:
            "A cap set at the price of the material excludes the production run, the freight and the customer consequences a bad lot actually causes. At minimum, carve defective material, breach of confidentiality and indemnity obligations out of the cap.",
        },
        {
          point: "Renewal, exit and last-time buy",
          detail:
            "Long automatic renewals with early notice deadlines quietly extend a relationship you decided to leave, and a supplier who can discontinue a material without a transition period leaves you with nothing to qualify an alternative against.",
        },
      ],
      redFlags: [
        "No order-of-precedence clause.",
        "Forecast converted into a binding commitment with no stated window.",
        "One-sided force majeure, or one broad enough to cover ordinary cost movement.",
        "Liability capped at the invoice value of the material.",
        "Automatic renewal with a long notice period required to escape.",
        "No quality requirements or specification incorporated by reference.",
      ],
      timeToComplete: "40 minutes to fill, plus one schedule per material · 2 to 3 weeks to agree",
    },

    placeholders: [
      { token: "[EFFECTIVE DATE]", label: "Effective date", example: "September 1, 2026" },
      { token: "[COMPANY NAME]", label: "Your legal entity name", brandKey: "legalEntityName", example: "Acme Beverage Co., LLC" },
      { token: "[COMPANY STATE]", label: "Your state of formation", brandKey: "stateOfFormation", example: "Delaware" },
      { token: "[COMPANY ENTITY TYPE]", label: "Your entity type", example: "limited liability company" },
      { token: "[COMPANY ADDRESS]", label: "Your address", brandKey: "principalAddress", example: "123 Main St, Austin, TX 78701" },
      { token: "[SUPPLIER NAME]", label: "Supplier's legal name", example: "Cascade Botanicals, Inc." },
      { token: "[SUPPLIER STATE]", label: "Supplier's state of formation", example: "Oregon" },
      { token: "[SUPPLIER ENTITY TYPE]", label: "Supplier's entity type", example: "corporation" },
      { token: "[SUPPLIER ADDRESS]", label: "Supplier's address", example: "1400 River Rd, Salem, OR 97301" },
      { token: "[BRAND NAME]", label: "Your brand name", brandKey: "tradeName", example: "Acme" },
      { token: "[PRODUCT CATEGORY]", label: "Product category", brandKey: "productCategory", example: "functional beverage" },
      { token: "[COVERED MATERIALS]", label: "Materials this agreement covers", example: "all ingredients and packaging components listed in a Schedule" },
      { token: "[COVERED SITES]", label: "Sites the agreement covers", example: "the Supplier's Salem, OR and Yakima, WA sites" },
      { token: "[FIRST SCHEDULE REFERENCE]", label: "Reference for the first schedule", example: "Schedule 1 — Yuzu juice concentrate" },
      { token: "[SCHEDULE APPROVERS]", label: "Who signs a schedule for each party", example: "an authorised signatory of each party" },
      { token: "[PAYMENT DAYS]", label: "Payment terms in days", example: "30 days" },
      { token: "[PRICE CHANGE NOTICE]", label: "Notice before a price change", example: "90 days" },
      { token: "[ESCALATION BASIS]", label: "Documented basis for any price change", example: "the index named in the applicable Schedule" },
      { token: "[FORECAST HORIZON]", label: "Rolling forecast horizon", example: "6 months" },
      { token: "[BINDING FORECAST WINDOW]", label: "Portion of the forecast that is binding", example: "the first 30 days" },
      { token: "[ORDER ACCEPTANCE DAYS]", label: "Days for the supplier to accept or reject an order", example: "3 business days" },
      { token: "[REJECTION WINDOW]", label: "Window to reject non-conforming material", example: "30 days from first use, and in any event within the material's shelf life" },
      { token: "[CHANGE NOTICE PERIOD]", label: "Notice of a change to material, site or sub-supplier", example: "60 days" },
      { token: "[ALLOCATION BASELINE]", label: "Volume protected during allocation", example: "the average monthly volume purchased over the preceding 12 months" },
      { token: "[TRACEABILITY RESPONSE TIME]", label: "Time to respond to a traceability request", example: "4 hours" },
      { token: "[LIABILITY CAP]", label: "Liability cap", example: "the amounts paid under the applicable Schedule in the preceding 12 months" },
      { token: "[INSURANCE REQUIREMENTS]", label: "Insurance the supplier must carry", example: "commercial general liability including products liability, with the Brand named as additional insured" },
      { token: "[TERM LENGTH]", label: "Initial term", example: "3 years" },
      { token: "[RENEWAL TERM]", label: "Renewal term", example: "1 year" },
      { token: "[TERMINATION NOTICE]", label: "Notice to terminate or not renew", example: "90 days" },
      { token: "[CURE PERIOD]", label: "Cure period for a material breach", example: "30 days" },
      { token: "[DISCONTINUATION NOTICE]", label: "Notice before discontinuing a material", example: "12 months" },
      { token: "[LAST-TIME-BUY WINDOW]", label: "Window to place a final order", example: "6 months from the discontinuation notice" },
      { token: "[GOVERNING LAW STATE]", label: "Governing law state", example: "Delaware" },
      { token: "[VENUE]", label: "Venue for disputes", example: "Wilmington, Delaware" },
    ],

    body: `This Master Supply Agreement (this "Agreement") is entered into as of [EFFECTIVE DATE] (the
"Effective Date") by and between:

  [COMPANY NAME], a [COMPANY STATE] [COMPANY ENTITY TYPE], with a principal place of business
  at [COMPANY ADDRESS] (the "Brand"); and

  [SUPPLIER NAME], a [SUPPLIER STATE] [SUPPLIER ENTITY TYPE], with a principal place of business
  at [SUPPLIER ADDRESS] (the "Supplier").

1. STRUCTURE AND SCOPE

1.1 This Agreement sets the standing terms on which the Supplier supplies [COVERED MATERIALS] to
    the Brand for [PRODUCT CATEGORY] products sold under the [BRAND NAME] brand, from
    [COVERED SITES].

1.2 Each material is covered only when the parties sign a schedule for it (each, a "Schedule").
    The first Schedule is [FIRST SCHEDULE REFERENCE]. A Schedule takes effect when signed by
    [SCHEDULE APPROVERS], and adding a Schedule does not require any other amendment to this
    Agreement.

1.3 Each Schedule shall state, for the material it covers: the material description and
    specification reference; price and price validity; minimum order quantity and any volume
    commitment; lead time; delivery terms and destination; certifications the Supplier maintains;
    lot documentation requirements; and any term specific to that material.

1.4 A Schedule may vary Sections 4 through 7 for the material it covers where it says so expressly.
    A Schedule may not vary Sections 8 through 14 unless the parties sign a written amendment to
    this Agreement identifying the section changed.

1.5 The Brand may direct that material be delivered to, and invoiced through, a manufacturer it
    designates in writing. The commercial relationship and the rights under this Agreement remain
    with the Brand.

2. ORDER OF PRECEDENCE

2.1 If there is a conflict between documents, the following order applies, highest first:
    (a) a written amendment to this Agreement signed by both parties;
    (b) this Agreement;
    (c) the applicable Schedule;
    (d) the specification incorporated by that Schedule; and
    (e) a purchase order issued by the Brand.

2.2 Pre-printed or standard terms appearing on any quotation, order acknowledgment, invoice,
    delivery note, packing list, website or portal have no effect, whether or not the receiving
    party objects to them, and are rejected.

2.3 A purchase order may set quantity, requested delivery date and destination. It may not vary
    price, specification, quality obligations, warranties, liability or termination terms.

3. ORDERS AND FORECASTING

3.1 The Brand issues purchase orders against a Schedule. The Supplier shall accept or reject each
    order within [ORDER ACCEPTANCE DAYS]; an order not rejected within that period is accepted.

3.2 The Supplier may reject an order only where it is inconsistent with the applicable Schedule.

3.3 The Brand shall provide a rolling forecast covering [FORECAST HORIZON], updated monthly.
    [BINDING FORECAST WINDOW] of each forecast is binding on both parties; the balance is a
    planning estimate that creates no purchase obligation.

3.4 The Supplier shall plan procurement and capacity against the full forecast horizon and shall
    notify the Brand promptly if it cannot support forecast volumes.

4. PRICE AND PAYMENT

4.1 Prices are as stated in each Schedule and are firm for the validity period stated there.

4.2 After a validity period expires, the Supplier may propose a price change only by reference to
    [ESCALATION BASIS], on [PRICE CHANGE NOTICE] written notice, stating the documented movement
    relied on.

4.3 If the Brand does not accept a proposed increase, the Brand may place a final order at the
    existing price for delivery within the lead time, and may terminate the affected Schedule on
    written notice without liability for any unfulfilled volume commitment under it.

4.4 Invoices are payable within [PAYMENT DAYS] of receipt. The Brand may withhold amounts genuinely
    disputed in good faith, provided it pays the undisputed balance and states the basis of the
    dispute.

5. QUALITY, DOCUMENTATION AND ACCEPTANCE

5.1 All material must conform to the specification incorporated by its Schedule. The Supplier shall
    provide a certificate of analysis with each lot identifying the lot code, parameters tested,
    methods used and actual values obtained.

5.2 The Supplier shall maintain the certifications stated in each Schedule and shall provide
    current documents on request and prompt written notice of any lapse, suspension or risk of
    lapse.

5.3 The Supplier shall provide and keep current the documentation relating to each material that
    the Brand reasonably requires to support its labelling, claims and customer requirements.
    Responsibility for the Brand's label content and claims rests with the Brand; responsibility
    for the accuracy and currency of the material documentation rests with the Supplier.

5.4 The Brand may reject non-conforming material by written notice within [REJECTION WINDOW]. On
    rejection the Supplier shall replace the material on an expedited basis at its cost or credit
    the price paid, at the Brand's election, and shall bear return or disposal costs.

5.5 Where non-conforming material is discovered only after use in production, the Supplier's
    responsibility extends to the reasonable, documented cost of the affected production run.

5.6 The Brand or its representative may audit the Supplier's sites and quality records on
    reasonable written notice during normal business hours.

6. CHANGE NOTIFICATION

6.1 The Supplier shall give the Brand at least [CHANGE NOTICE PERIOD] written notice, and obtain
    the Brand's written approval, before any change to a specification, the composition or grade of
    a material, the country or region of origin, the production site, any sub-supplier, the
    allergen profile of the production environment, or any certification status.

6.2 The Supplier shall not substitute alternative material without the Brand's prior written
    approval, whatever the reason.

7. SUPPLY INTERRUPTION AND ALLOCATION

7.1 The Supplier shall notify the Brand promptly of any actual or anticipated interruption to
    supply, stating the cause, expected duration and planned mitigation.

7.2 If the Supplier cannot meet total demand for a material, it shall allocate available material
    so that the Brand receives no less than [ALLOCATION BASELINE], and shall not de-prioritise the
    Brand by reason of its order size relative to other customers.

7.3 During any interruption the Brand may source the material or a substitute elsewhere without
    breaching any exclusivity or volume commitment, and such purchases count toward any volume
    commitment.

7.4 Neither party is liable for a failure caused by an event beyond its reasonable control,
    provided it notifies the other promptly and works to resume performance. Ordinary movements in
    market price, currency, labour cost or a party's own costs are not such an event. This
    Section 7.4 applies equally to both parties.

8. RECALL AND TRACEABILITY

8.1 The Supplier shall respond to a traceability request within [TRACEABILITY RESPONSE TIME],
    identifying the lots supplied, their source materials and their destinations.

8.2 Each party shall notify the other promptly on becoming aware of a matter that may lead to a
    withdrawal or recall of a product containing a covered material, and shall cooperate promptly
    and fully in any withdrawal, recall or investigation.

8.3 Costs follow fault: each party bears the costs attributable to its own breach, defective
    material, or negligent act or omission. Decisions about whether to conduct a withdrawal or
    recall of the Brand's finished product rest with the Brand.

9. WARRANTIES, INDEMNITY, LIABILITY AND INSURANCE

9.1 The Supplier warrants that material supplied conforms to its specification, is produced under
    sanitary conditions consistent with the certifications it maintains, is fit for the use
    contemplated by the applicable Schedule, and is free of liens.

9.2 Each party shall indemnify the other against third-party claims arising from its own breach of
    this Agreement or its negligent or wilful acts.

9.3 Except for the matters in Section 9.4, each party's aggregate liability under this Agreement is
    limited to [LIABILITY CAP].

9.4 The cap in Section 9.3 does not apply to: amounts payable for material supplied; the Supplier's
    obligations under Sections 5.4, 5.5 and 8.3; a party's indemnity obligations; breach of
    confidentiality; or a party's gross negligence or wilful misconduct.

9.5 The Supplier shall maintain [INSURANCE REQUIREMENTS] and shall provide certificates of
    insurance on request and on renewal.

10. CONFIDENTIALITY

10.1 Each party shall hold the other's non-public information in confidence, use it only for this
     Agreement, and disclose it only to personnel and advisers who need it and who are bound by
     obligations at least as protective as this Section 10. Obligations with respect to information
     that constitutes a trade secret continue for as long as it remains a trade secret.

11. TERM AND TERMINATION

11.1 This Agreement begins on the Effective Date and runs for [TERM LENGTH], renewing automatically
     for successive [RENEWAL TERM] periods unless either party gives [TERMINATION NOTICE] written
     notice of non-renewal. A Schedule runs for the term stated in it, and in any event no longer
     than this Agreement unless the parties agree otherwise in that Schedule.

11.2 Either party may terminate this Agreement or any Schedule on written notice if the other
     materially breaches and does not cure within [CURE PERIOD] after notice.

11.3 The Supplier shall give at least [DISCONTINUATION NOTICE] written notice before discontinuing
     a covered material or ceasing to supply it to the Brand. Following that notice, or following
     termination by the Brand for the Supplier's breach, the Brand may place final orders during
     [LAST-TIME-BUY WINDOW] at the price then in effect, in any quantity the Brand specifies.

11.4 On termination the Supplier shall fulfil all accepted orders, deliver current specifications
     and all certification and traceability documentation relating to material supplied, return any
     Brand property and tooling in its possession, and provide reasonable assistance in qualifying
     an alternative source.

11.5 Sections 5, 8, 9, 10, 12 and 13 survive termination or expiry.

12. GOVERNING LAW AND VENUE

12.1 This Agreement is governed by the laws of the State of [GOVERNING LAW STATE], without regard
     to its conflict of laws principles. The parties consent to venue in [VENUE].

13. GENERAL

13.1 This Agreement, together with each signed Schedule and each signed amendment, is the entire
     agreement between the parties on its subject matter and supersedes all prior terms of
     purchase and sale between them for the covered materials. Neither party may assign it without
     the other's prior written consent, except that either party may assign to a successor in
     connection with a merger or sale of substantially all assets. The Supplier is an independent
     contractor. Notices must be in writing. If any provision is held unenforceable, the remainder
     continues in effect. This Agreement may be signed in counterparts, including by electronic
     signature, each of which is an original.

IN WITNESS WHEREOF, the parties have executed this Agreement as of the Effective Date.


[COMPANY NAME]                            [SUPPLIER NAME]


By: _______________________________       By: _______________________________

Name: _____________________________       Name: _____________________________

Title: ____________________________       Title: ____________________________

Date: _____________________________       Date: _____________________________


─────────────────────────────────────────────────────────────────────────────
SCHEDULE FORM — one per material
─────────────────────────────────────────────────────────────────────────────

Schedule number and date .................. __________________________________
Material description ...................... __________________________________
Specification reference and version ....... __________________________________
Price and price unit ...................... __________________________________
Price validity period ..................... __________________________________
Escalation index or basis ................. __________________________________
Minimum order quantity .................... __________________________________
Volume commitment, if any ................. __________________________________
Lead time ................................. __________________________________
Delivery terms and destination ............ __________________________________
Certifications maintained ................. __________________________________
Lot documentation required ................ __________________________________
Shelf life on delivery .................... __________________________________
Material-specific terms ................... __________________________________

Signed for the Brand ...................... __________________________________

Signed for the Supplier ................... __________________________________
`,
  },
];

export default SUPPLY_TEMPLATES;
